
import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "@/hooks/use-toast";
import { useQueryClient } from "@tanstack/react-query";
import { toast as sonnerToast } from "sonner";
import { formatDateForDatabase } from "@/utils/dateHelpers";

interface ProfileData {
  first_name: string;
  last_name: string;
  birthdate: Date | null;
  username: string;
}

interface EntryReassignmentResult {
  moved_count: number;
  moved_entries: Array<{
    entry_id: string;
    title: string;
    year: number;
    age: number;
    from_section: string;
  }>;
}

export const useProfile = () => {
  const { user, signOut } = useAuth();
  const [profile, setProfile] = useState<ProfileData | null>(null);
  const [loading, setLoading] = useState(true);
  const queryClient = useQueryClient();

  const fetchProfile = async () => {
    if (!user) return;
    
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('profiles')
        .select('first_name, last_name, birthdate, username')
        .eq('id', user.id)
        .single();

      if (error) throw error;

      setProfile({
        first_name: data.first_name || '',
        last_name: data.last_name || '',
        birthdate: data.birthdate ? new Date(data.birthdate) : null,
        username: data.username || ''
      });
    } catch (error: any) {
      console.error('Error fetching profile:', error);
      
      // Check if this is a "no rows returned" error (profile doesn't exist)
      if (error?.code === 'PGRST116' || error?.message?.includes('JSON object requested, multiple (or no) rows returned')) {
        console.warn('Profile not found for authenticated user, signing out');
        sonnerToast.error("Ihr Profil wurde nicht gefunden. Sie werden abgemeldet.", {
          description: "Bitte registrieren Sie sich erneut oder kontaktieren Sie den Support."
        });
        
        // Sign out the user after a brief delay
        setTimeout(() => {
          signOut();
        }, 2000);
        return;
      }
      
      // Handle other errors normally
      toast({
        title: "Fehler",
        description: "Profil konnte nicht geladen werden.",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const reassignTimelineEntries = async (newBirthdate: Date) => {
    try {
      console.log('🔄 Reassigning timeline entries for new birthdate:', newBirthdate);
      
      const { data, error } = await supabase.rpc('reassign_timeline_entries_after_birthdate_change', {
        target_user_id: user!.id,
        new_birthdate: formatDateForDatabase(newBirthdate)
      });

      if (error) {
        console.error('Error reassigning timeline entries:', error);
        throw error;
      }

      const result = data?.[0] as EntryReassignmentResult;
      console.log('📊 Timeline entries reassignment result:', result);

      if (result?.moved_count > 0) {
        sonnerToast.success(
          `Timeline aktualisiert: ${result.moved_count} Eintrag${result.moved_count > 1 ? 'e' : ''} wurde${result.moved_count > 1 ? 'n' : ''} in die richtige${result.moved_count > 1 ? 'n' : ''} Lebensphase${result.moved_count > 1 ? 'n' : ''} verschoben.`,
          {
            duration: 5000,
            description: 'Ihre Timeline wurde automatisch an Ihr neues Geburtsdatum angepasst.'
          }
        );
      } else {
        console.log('ℹ️ No timeline entries needed to be moved');
      }

      return result;
    } catch (error) {
      console.error('Failed to reassign timeline entries:', error);
      sonnerToast.error('Fehler beim Aktualisieren der Timeline', {
        description: 'Die Timeline-Einträge konnten nicht neu zugeordnet werden.'
      });
      throw error;
    }
  };

  const invalidateTimelineCaches = () => {
    console.log('🗑️ Invalidating timeline-related caches');
    // Invalidate all timeline-related queries
    queryClient.invalidateQueries({ queryKey: ['profile-for-timeline'] });
    queryClient.invalidateQueries({ queryKey: ['timeline-data'] });
    queryClient.invalidateQueries({ queryKey: ['timeline-entries'] });
    queryClient.invalidateQueries({ queryKey: ['timeline'] });
    queryClient.invalidateQueries({ queryKey: ['timeline-access'] });
    queryClient.invalidateQueries({ queryKey: ['profile-timeline'] });
  };

  const updateProfile = async (updates: Partial<ProfileData>) => {
    if (!user) return;

    const isBirthdateUpdate = updates.birthdate !== undefined;
    const oldBirthdate = profile?.birthdate;
    const newBirthdate = updates.birthdate;

    try {
      const updateData: any = {};
      
      if (updates.first_name !== undefined) updateData.first_name = updates.first_name;
      if (updates.last_name !== undefined) updateData.last_name = updates.last_name;
      if (updates.birthdate !== undefined) {
        updateData.birthdate = updates.birthdate ? formatDateForDatabase(updates.birthdate) : null;
      }

      const { error } = await supabase
        .from('profiles')
        .update(updateData)
        .eq('id', user.id);

      if (error) throw error;

      // Update local state
      setProfile(prev => prev ? { ...prev, ...updates } : null);

      // Handle birthdate-specific updates
      if (isBirthdateUpdate && newBirthdate && (!oldBirthdate || oldBirthdate.getTime() !== newBirthdate.getTime())) {
        console.log('🎂 Birthdate changed, updating timeline...');
        
        // Show immediate feedback
        sonnerToast.loading('Timeline wird aktualisiert...', {
          id: 'timeline-update',
          description: 'Einträge werden den neuen Lebensphasen zugeordnet'
        });

        try {
          // Reassign timeline entries
          await reassignTimelineEntries(newBirthdate);
          
          // Invalidate caches to trigger refetch
          invalidateTimelineCaches();
          
          // Dismiss loading toast
          sonnerToast.dismiss('timeline-update');
        } catch (reassignError) {
          sonnerToast.dismiss('timeline-update');
          // Don't throw here - profile update was successful
          console.error('Timeline reassignment failed, but profile was updated:', reassignError);
        }
      }
      
      toast({
        title: "Erfolgreich",
        description: "Profil wurde aktualisiert.",
      });

      return true;
    } catch (error) {
      console.error('Error updating profile:', error);
      toast({
        title: "Fehler",
        description: "Profil konnte nicht aktualisiert werden.",
        variant: "destructive"
      });
      return false;
    }
  };

  const updateEmail = async (newEmail: string) => {
    try {
      const { error } = await supabase.auth.updateUser({ email: newEmail });
      
      if (error) throw error;
      
      toast({
        title: "Bestätigungs-E-Mail gesendet",
        description: "Bitte bestätigen Sie Ihre neue E-Mail-Adresse.",
      });
      
      return true;
    } catch (error) {
      console.error('Error updating email:', error);
      toast({
        title: "Fehler",
        description: "E-Mail konnte nicht aktualisiert werden.",
        variant: "destructive"
      });
      return false;
    }
  };

  useEffect(() => {
    fetchProfile();
  }, [user]);

  return {
    profile,
    loading,
    updateProfile,
    updateEmail,
    refreshProfile: fetchProfile
  };
};
